import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { ApiError } from '../shared/api/HttpClient';
import { localizedProblem } from '../shared/i18n';
import { StationsApi } from './StationsApi';
import type { ManagedStation, StationCapacitySummary } from './types';

const emptyCapacity: StationCapacitySummary = { total: 0, enabled: 0, maximumActive: 0, availableActive: 0 };

export function StationsWorkspace(): ReactElement {
  const api = useMemo(() => new StationsApi(), []);
  const [stations, setStations] = useState<ManagedStation[]>([]);
  const [capacity, setCapacity] = useState(emptyCapacity);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [timezone, setTimezone] = useState('Europe/Warsaw');

  const refresh = useCallback(async (): Promise<void> => {
    const [nextStations, nextCapacity] = await Promise.all([api.list(), api.capacity()]);
    setStations(nextStations);
    setCapacity(nextCapacity);
  }, [api]);

  useEffect(() => {
    void refresh().catch((reason: unknown) => setError(problem(reason))).finally(() => setLoading(false));
  }, [refresh]);

  const create = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setPending('create');
    setError(null);
    setNotice(null);
    try {
      const station = await api.create({
        name: name.trim(),
        slug: slug.trim().toLowerCase(),
        timezone,
        schedulerHorizonMinutes: 180,
        maxLiveGuests: 4,
      });
      setName('');
      setSlug('');
      await refresh();
      setNotice(`Станцію «${station.name}» створено вимкненою. Підготуйте медіатеку й аварійний список, а потім увімкніть її.`);
    } catch (reason: unknown) {
      setError(problem(reason));
    } finally {
      setPending(null);
    }
  };

  const toggle = async (station: ManagedStation): Promise<void> => {
    setPending(station.id);
    setError(null);
    setNotice(null);
    try {
      await api.setEnabled(station, !station.enabled);
      await refresh();
      setNotice(station.enabled ? `Станцію «${station.name}» вимкнено.` : `Станцію «${station.name}» допущено до ефіру.`);
    } catch (reason: unknown) {
      setError(problem(reason));
      await refresh().catch(() => undefined);
    } finally {
      setPending(null);
    }
  };

  return <main className="stations-room">
    <header className="stations-header">
      <div><p className="eyebrow">КОНФІГУРАЦІЯ ПЛАТФОРМИ</p><h1>Радіостанції</h1><p>Створюйте станції без жорсткого продуктового ліміту. Одночасний ефір обмежується безпечною місткістю цього медіавузла.</p></div>
      <div className="capacity-gauge" aria-label={`Активні станції: ${capacity.enabled} із ${capacity.maximumActive}`}><span>МІСТКІСТЬ ВУЗЛА</span><strong>{capacity.enabled} / {capacity.maximumActive}</strong><small>{capacity.availableActive} вільних місць</small></div>
    </header>
    {error !== null && <div className="message error" role="alert"><span>ДІЮ НЕ ВИКОНАНО</span>{error}<button type="button" onClick={() => setError(null)}>Закрити</button></div>}
    {notice !== null && <div className="message success" role="status"><span>ГОТОВО</span>{notice}<button type="button" onClick={() => setNotice(null)}>Закрити</button></div>}
    <section className="station-admission-note">
      <strong>Як працює масштабування</strong>
      <p>Станцій у базі може бути більше, ніж активних місць. Платформа не дозволить увімкнути нову станцію, коли вузол заповнений, тому вже активні ефіри не постраждають. Для десятків станцій додаються окремі медіавузли; підняття одного числа на перевантаженому сервері не використовується.</p>
    </section>
    <div className="stations-layout">
      <section className="station-list" aria-labelledby="station-list-title">
        <header><div><p className="eyebrow">НА ЦІЙ ІНСТАЛЯЦІЇ</p><h2 id="station-list-title">Створені станції</h2></div><span>{capacity.total} усього</span></header>
        {loading ? <div className="workspace-loading">Завантажую станції…</div> : stations.length === 0 ? <p className="stations-empty">Станцій ще немає.</p> : stations.map((station) => <article key={station.id} className="managed-station">
          <div><span className={station.enabled ? 'station-live' : 'station-off'}>{station.enabled ? 'УВІМКНЕНО' : 'ВИМКНЕНО'}</span><h3>{station.name}</h3><code>{station.slug}</code></div>
          <dl><div><dt>Часовий пояс</dt><dd>{station.timezone}</dd></div><div><dt>Потік</dt><dd>{station.format.bitrateKbps} кбіт/с · {station.format.channels === 2 ? 'стерео' : 'моно'}</dd></div><div><dt>Планування</dt><dd>{station.schedulerHorizonMinutes} хв наперед</dd></div></dl>
          <div className="managed-station-actions"><a href={`/admin?station=${station.id}`}>Розклад</a><a href={`/admin/library?station=${station.id}`}>Медіатека</a><button type="button" disabled={pending !== null || (!station.enabled && capacity.availableActive === 0)} onClick={() => void toggle(station)}>{pending === station.id ? 'ЗАЧЕКАЙТЕ…' : station.enabled ? 'ВИМКНУТИ' : capacity.availableActive === 0 ? 'НЕМАЄ МІСЦЯ' : 'УВІМКНУТИ'}</button></div>
        </article>)}
      </section>
      <form className="station-create" onSubmit={(event) => void create(event)}>
        <p className="eyebrow">НОВА СТАНЦІЯ</p><h2>Створити станцію</h2><p>Нова станція створюється вимкненою і не займає обчислювальні ресурси, доки ви її не підготуєте.</p>
        <label htmlFor="station-name">Назва</label><input id="station-name" required minLength={1} maxLength={160} value={name} onChange={(event) => { setName(event.target.value); if (slug === '' || slug === slugify(name)) setSlug(slugify(event.target.value)); }} placeholder="Наприклад, Sweet Lana Hits" />
        <label htmlFor="station-slug">Адреса потоку</label><input id="station-slug" required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value={slug} onChange={(event) => setSlug(event.target.value.toLowerCase())} placeholder="sweet-lana-hits" /><small>Латинські літери, цифри та дефіси. Після створення адреса не змінюється.</small>
        <label htmlFor="station-timezone">Часовий пояс</label><select id="station-timezone" value={timezone} onChange={(event) => setTimezone(event.target.value)}><option value="Europe/Warsaw">Європа / Варшава</option><option value="Europe/Kyiv">Європа / Київ</option><option value="UTC">UTC</option></select>
        <button className="button primary" type="submit" disabled={pending !== null}>{pending === 'create' ? 'СТВОРЮЮ…' : 'СТВОРИТИ СТАНЦІЮ'}</button>
      </form>
    </div>
  </main>;
}

function slugify(value: string): string {
  return value.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function problem(reason: unknown): string {
  return reason instanceof ApiError ? localizedProblem(reason.problem) : 'Не вдалося виконати дію. Перевірте з’єднання й повторіть спробу.';
}
